import { Grow, Popper } from '@material-ui/core';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import { allowedModules, APP_MODULES } from 'constants/appModules';
import { PATHS } from 'constants/paths';
import { FC, MouseEvent, MutableRefObject } from 'react';
import { useIntl } from 'react-intl';
import { NavLink } from 'react-router-dom';
import { useSelectUser } from 'redux/User/selectors';
import { Profiles } from 'types/user';

import styles from './ProfilePopup.module.scss';
import IsBetaUserToggle from './components/IsBetaUserToggle';
import TimezoneSelect from './components/TimezoneSelect';
import UnitSelect from './components/UnitSelect';

interface Props {
  isOpen: boolean;
  anchorRef: MutableRefObject<HTMLButtonElement>;
  onHideProfilePopupClick: (event: MouseEvent<EventTarget>) => void;
}

const ProfilePopup: FC<Props> = ({ isOpen, anchorRef, onHideProfilePopupClick }) => {
  const intl = useIntl();
  const user = useSelectUser();

  return (
    <Popper
      open={isOpen}
      anchorEl={anchorRef.current}
      transition
      disablePortal
      className={styles.profileContainer}
    >
      {({ TransitionProps }) => (
        <Grow
          {...TransitionProps}
          style={{
            transformOrigin: 'center top',
          }}
        >
          <Paper elevation={3} variant="outlined" className={styles.profilePaper}>
            <ClickAwayListener onClickAway={onHideProfilePopupClick}>
              <div>
                <Grid container alignItems="center" justify="space-between">
                  {user && (
                    <span
                      className={styles.profileName}
                    >{`${user.firstName} ${user.lastName}`}</span>
                  )}
                  {/*  TODO: add logout url
                  <span onClick={signoutUser} className={styles.signOutLink}>
                    {intl.formatMessage({ id: 'header.profile.signOut' })}
                  </span>
                  */}
                </Grid>
                <div className={styles.divider} />
                <TimezoneSelect />
                <div className={styles.divider} />
                <UnitSelect />
                <div className={styles.divider} />
                {user &&
                  ([Profiles.PO, Profiles.DRILLX].includes(user.profile) ||
                    user.isBeta === true) && (
                    <>
                      <IsBetaUserToggle />
                      <div className={styles.divider} />
                    </>
                  )}
                {user && (
                  <>
                    {allowedModules[user.profile].includes(APP_MODULES.USER_ADMIN) && (
                      <>
                        <NavLink
                          exact
                          activeClassName={styles.activeLink}
                          className={styles.link}
                          to={PATHS.USER_ADMIN}
                          onClick={onHideProfilePopupClick}
                        >
                          {intl.formatMessage({
                            id: 'header.profile.manageUsers',
                          })}
                        </NavLink>
                        <div className={styles.divider} />
                      </>
                    )}
                    {allowedModules[user.profile].includes(APP_MODULES.WELLBORE_ADMIN) && (
                      <>
                        <NavLink
                          exact
                          activeClassName={styles.activeLink}
                          className={styles.link}
                          to={PATHS.WELLBORE_ADMIN}
                          onClick={onHideProfilePopupClick}
                        >
                          {intl.formatMessage({
                            id: 'header.profile.manageWellbores',
                          })}
                        </NavLink>
                        <div className={styles.divider} />
                      </>
                    )}
                    <NavLink
                      activeClassName={styles.activeLink}
                      className={styles.link}
                      to={PATHS.DATA_MANAGEMENT}
                      onClick={onHideProfilePopupClick}
                    >
                      {intl.formatMessage({ id: 'header.pages.DATA_MANAGEMENT' })}
                    </NavLink>
                    <div className={styles.divider} />
                    {allowedModules[user.profile].includes(APP_MODULES.DDX_PO) && (
                      <>
                        <NavLink
                          activeClassName={styles.activeLink}
                          className={styles.link}
                          to={PATHS.DDX_PO}
                          onClick={onHideProfilePopupClick}
                        >
                          {intl.formatMessage({ id: 'header.profile.ddx' })}
                        </NavLink>
                        <div className={styles.divider} />
                      </>
                    )}
                    {allowedModules[user.profile].includes(APP_MODULES.ADMIN_PO) && (
                      <>
                        <NavLink
                          activeClassName={styles.activeLink}
                          className={styles.link}
                          to={PATHS.ADMIN_PO}
                          onClick={onHideProfilePopupClick}
                        >
                          {intl.formatMessage({ id: 'header.profile.adminPo' })}
                        </NavLink>
                        <div className={styles.divider} />
                      </>
                    )}
                  </>
                )}
              </div>
            </ClickAwayListener>
          </Paper>
        </Grow>
      )}
    </Popper>
  );
};

export default ProfilePopup;
